import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Stack,
  Title,
  Text,
  UnstyledButton,
  Divider,
  Tooltip,
  Flex,
  Modal,
  ScrollArea,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconChevronLeft, IconChevronRight, IconHome, IconFileText, IconChartBar, IconEye, IconGridDots, IconSettings } from '@tabler/icons-react';
import UserAccountButton from '../components/UserAccountButton';
import ViewMatrixPage from '../components/ViewMatrixPage';

import { useActivePermission } from '../hooks/useActivePermission';
import { mapPermissionsToSelectOptions, findPermissionByKey, buildPermissionKey } from '../utils/permissionHelpers';
import { SetActivePermission } from '../actions/authActions';
import useFeatureAccess from '../hooks/useFeatureAccess';

const Sidebar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { user, permissions } = useSelector((state) => state.auth);
  const activePermission = useActivePermission();

  const [collapsed, setCollapsed] = useState(false);
  const [opened, setOpened] = useState(false);
  const isMobile = useMediaQuery('(max-width: 50em)');

  const permissionOptions = mapPermissionsToSelectOptions(permissions.data);
  const currentPermissionValue = activePermission ? buildPermissionKey(activePermission) : '';

  const handlePermissionChange = (value) => {
    const selected = findPermissionByKey(permissions.data, value);
    if (selected) {
      dispatch(SetActivePermission(selected));
      navigate('/backoffice/home')
    }
  };
  
  /*const handleChangeTab = (tabId) => {
    const item = navItems.find((nav) => nav.id === tabId);

    if (!item) return;

    if (item.modal) {
      setOpened(true);
    } else if (item.path) {
      navigate(item.path);
    }
  };*/

  /*const isNavEnabled = (id, activeProcess, user) => {
    const estado = activeProcess?.estado.Id.toUpperCase() || null;

    switch (id) {
      case "kpi":
      case "admin":
      case "home":
        return true;

      case "evidences":
        return estado === APP_STATES.ACTIVO || estado === APP_STATES.HISTORICO;

      case "evaluacion":
        return true //estado === APP_STATES.ACTIVO;

      case "vision":
        return estado === APP_STATES.ACTIVO || estado === APP_STATES.HISTORICO;

      default:
        return true;
    }
  };*/

  const navItems = [
    { id: 'home', name: 'Inicio', icon: IconHome, path: '/backoffice/home' },
    { id: 'evidences', name: 'Gestionar Evidencias', icon: IconFileText, path: '/backoffice/evidences' },
    { id: 'evaluacion', name: 'Proceso Evaluación', icon: IconChartBar, path: '/backoffice/evaluation',},
    { id: 'vision', name: 'Visión General', icon: IconEye, path: '/backoffice/evaluation/overview',},
    { id: 'kpi', name: 'Matriz KPI', icon: IconGridDots, modal: 'matrix' },
    ...(user.roles.includes('administrador') ? [{ id: 'admin', name: 'Administracion', icon: IconSettings, path: '/admin' }] : []),
  ];

  function SidebarItem({ item, collapsed, onOpenMatrix }) {
    const { canEnter, canEdit, readOnly, reason } = useFeatureAccess(item.id);

    const disabled = !canEnter && item.modal !== 'matrix'; // kpi modal siempre entra
    const onClick = () => {
      if (item.modal === 'matrix') return onOpenMatrix();
      if (!canEnter) return;
      navigate(item.path);
    };

    return (
      <Tooltip
        label={item.name}
        position="right"
        offset={18}
        withArrow
        disabled={!collapsed}
        key={item.id}
      >
        <UnstyledButton
          className={`nav-item ${disabled ? 'disabled' : ''}`}
          onClick={onClick}
          disabled={disabled}
          style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
        >
          <item.icon size={20} />
          {!collapsed && <Text size="md">{item.name}</Text>}
        </UnstyledButton>
      </Tooltip>
    );
  }

  return (
    <>
      <Box className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        <Box>
          <UnstyledButton onClick={() => setCollapsed((c) => !c)} w="100%">
            <Flex justify="space-between" align="center">
              <Title order={2} fw={500} c="white">PEI</Title>
              <Box className="icon-subtle">
                {collapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
              </Box>
            </Flex>
          </UnstyledButton>
          <Divider my="xs" color="gray.8" />
        </Box>

        <ScrollArea mih={150}>
          <Stack spacing="xs" mt="md" className="nav-section">
            {navItems.map((item) => (
              <SidebarItem
                key={item.id}
                item={item}
                collapsed={collapsed}
                onOpenMatrix={() => setOpened(true)}
              />
            ))}
          </Stack>
        </ScrollArea>

        <Box mt="auto">
          <Divider my="xs" color="gray.8" />
          <UserAccountButton
            collapsed={collapsed}
            user={user}
            activePermission={activePermission}
            onPermissionChange={handlePermissionChange}
            currentPermission={currentPermissionValue}
            permissionOptions={permissionOptions}
          />
        </Box>
      </Box>

      <Modal
        size="70%"
        opened={opened}
        onClose={() => setOpened(false)}
        overlayProps={{ backgroundOpacity: 0.35, blur: 1 }}
        fullScreen={isMobile}
        title={
          <Text size="xl" fw={700} c="#d8175b">
            MATRIZ DE KPI PARA MONITOREO DEL PLAN ESTRATÉGICO INSTITUCIONAL
          </Text>
        }
      >
        <Box p="lg">
          <ViewMatrixPage />
        </Box>
      </Modal>
    </>
  );
}

export default Sidebar;
